/**
 * Options handed to the Rust `clip_url` command so the in-webview
 * loading overlay, window title, "Saved" page, and native window
 * background all match Minerva's current theme + UI language.
 *
 * Each `_()` call is a literal string so the i18next scanner can
 * extract the keys — keep them inline here rather than building from
 * dynamic input. The theme `bg`/`fg` come from the same
 * `getThemeCode()` that paints the rest of the app, so a user on
 * light, dark, eink, or a custom palette sees the same chrome in the
 * clipper window.
 */

import { getThemeCode } from '@/utils/style';

type Translate = (key: string) => string;

export interface ClipOptions {
  windowTitle: string;
  overlayTitle: string;
  loadingStatus: string;
  capturingStatus: string;
  savedTitle: string;
  background: string;
  foreground: string;
  /** Interactive mode: show the page with a Cancel/Capture bar instead of
   *  the opaque overlay, so the user can sign in before capturing. Mobile
   *  only — the desktop flow ignores it. */
  interactive?: boolean;
  signInHint: string;
  captureLabel: string;
  cancelLabel: string;
}

export function getClipOptions(_: Translate): ClipOptions {
  const { bg, fg } = getThemeCode();
  return {
    windowTitle: _('Saving to your Minerva library…'),
    overlayTitle: _('Saving to Minerva'),
    loadingStatus: _('Loading article…'),
    capturingStatus: _('Capturing article…'),
    savedTitle: _('Saved to Minerva'),
    background: bg,
    foreground: fg,
    signInHint: _('Sign in if needed, then capture'),
    captureLabel: _('Capture'),
    cancelLabel: _('Cancel'),
  };
}
